import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useWallet } from '../hooks/useWallet';

const AMTS_PRICE = 7.50;

export default function Dashboard() {
  const { address, balance, walletType, refresh, REST, blockHeight, nativeWalletRef, disconnect } = useWallet();
  const [allBalances, setAllBalances] = useState([]);
  const [txs,         setTxs]         = useState([]);
  const [netStats,    setNetStats]     = useState({});
  const [loadingTx,   setLoadingTx]   = useState(false);
  const [allTxs,      setAllTxs]      = useState([]);
  const [loadingAllTx,setLoadingAllTx]= useState(false);
  const [chainTxs,    setChainTxs]    = useState([]);
  const [loadingChain,setLoadingChain]= useState(false);
  // Send form
  const [sendTo,      setSendTo]      = useState('');
  const [sendAmt,     setSendAmt]     = useState('');
  const [sendMemo,    setSendMemo]    = useState('');
  const [sendLoading, setSendLoading] = useState(false);
  const [sendResult,  setSendResult]  = useState(null);
  const [showSend,    setShowSend]    = useState(false);

  const fmtBal = (balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  const usd    = '$' + ((balance || 0) * AMTS_PRICE).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  useEffect(() => {
    loadNetStats();
    loadChainTxs();
  }, []);

  // Refresh chain txs every 15s
  useEffect(() => {
    const id = setInterval(loadChainTxs, 15000);
    return () => clearInterval(id);
  }, []);

  // Auto-refresh tx history every 30s
  useEffect(() => {
    if (!address) return;
    const id = setInterval(loadAllTxs, 30000);
    return () => clearInterval(id);
  }, [address]);

  useEffect(() => {
    if (address) {
      loadBalances();
      loadTxs();
      loadAllTxs();
    } else {
      setAllBalances([]);
      setTxs([]);
      setAllTxs([]);
    }
  }, [address]);

  async function loadNetStats() {
    try {
      const [vRes, pRes] = await Promise.all([
        fetch(`${REST}/cosmos/staking/v1beta1/validators?status=BOND_STATUS_BONDED&pagination.limit=25`),
        fetch(`${REST}/cosmos/distribution/v1beta1/community_pool`),
      ]);
      const vData = await vRes.json();
      const pData = await pRes.json();
      const poolAmt = (pData.pool || []).find(c => c.denom === 'uamts');
      setNetStats({
        validators: vData.validators?.length || 0,
        pool: poolAmt ? (parseFloat(poolAmt.amount) / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—',
      });
    } catch {}
  }

  async function loadBalances() {
    try {
      const res  = await fetch(`${REST}/cosmos/bank/v1beta1/balances/${address}`);
      const data = await res.json();
      setAllBalances(data.balances || []);
    } catch {}
  }

  async function rpcSearch(query, perPage = 10) {
    // All tx queries go through api.amitis.network/rpc proxy to avoid CORS
    const url = `https://api.amitis.network/rpc?endpoint=tx_search&query=%22${query}%22&per_page=${perPage}&order_by=%22desc%22`;
    const res  = await fetch(url);
    const data = await res.json();
    const txs  = data.result?.txs || [];
    // Fetch full tx details from REST
    return Promise.all(txs.map(async tx => {
      try {
        const r = await fetch(`${REST}/cosmos/tx/v1beta1/txs/${tx.hash}`);
        const d = await r.json();
        return d.tx_response || { txhash: tx.hash, height: tx.height, code: tx.tx_result?.code || 0 };
      } catch {
        return { txhash: tx.hash, height: tx.height, code: tx.tx_result?.code || 0 };
      }
    }));
  }

  async function loadTxs() {
    if (!address) return;
    setLoadingTx(true);
    try {
      const [sent, received] = await Promise.all([
        rpcSearch(`message.sender='${address}'`, 5),
        rpcSearch(`coin_received.receiver='${address}'`, 5),
      ]);
      const map = new Map();
      for (const tx of [...sent, ...received]) {
        if (tx?.txhash) map.set(tx.txhash, tx);
      }
      const merged = Array.from(map.values())
        .sort((a, b) => parseInt(b.height || 0) - parseInt(a.height || 0))
        .slice(0, 5);
      setTxs(merged);
    } catch {}
    setLoadingTx(false);
  }

  async function loadAllTxs() {
    if (!address) return;
    setLoadingAllTx(true);
    try {
      // Search sent and received
      const [sent, received] = await Promise.all([
        rpcSearch(`message.sender='${address}'`, 25),
        rpcSearch(`coin_received.receiver='${address}'`, 25),
      ]);
      // Merge and dedup
      const map = new Map();
      for (const tx of [...sent, ...received]) {
        if (tx?.txhash) map.set(tx.txhash, tx);
      }
      const merged = Array.from(map.values())
        .sort((a, b) => parseInt(b.height || 0) - parseInt(a.height || 0))
        .slice(0, 25);
      setAllTxs(merged);
    } catch (e) {
      console.error('loadAllTxs error:', e);
    }
    setLoadingAllTx(false);
  }

  async function loadChainTxs() {
    setLoadingChain(true);
    try {
      // Get latest block height then search recent txs
      const statusRes  = await fetch(`${REST}/cosmos/base/tendermint/v1beta1/blocks/latest`);
      const statusData = await statusRes.json();
      const latest     = parseInt(statusData.block?.header?.height || 0);
      if (!latest) { setLoadingChain(false); return; }
      const results = await rpcSearch(`tx.height>${latest - 500}`, 25);
      setChainTxs(results.filter(Boolean));
    } catch (e) {
      console.error('loadChainTxs error:', e);
    }
    setLoadingChain(false);
  }

  function getAmt(denom) {
    const b = allBalances.find(b => b.denom === denom);
    return b ? parseInt(b.amount) / 1_000_000 : 0;
  }

  async function handleSend(e) {
    e.preventDefault();
    if (!sendTo || !sendAmt) return;
    setSendLoading(true);
    setSendResult(null);
    try {
      let result;
      if (walletType === 'native' && nativeWalletRef) {
        result = await nativeWalletRef.send(sendTo, sendAmt, sendMemo);
      } else if (walletType === 'keplr' && window.keplr) {
        // Keplr send via CosmJS
        const { SigningStargateClient, GasPrice } = await import('@cosmjs/stargate');
        const signer = window.keplr.getOfflineSigner('amitis-network');
        const client = await SigningStargateClient.connectWithSigner(
          'https://rpc.amitis.network', signer,
          { gasPrice: GasPrice.fromString('0.025uamts') }
        );
        const uamts = Math.floor(parseFloat(sendAmt) * 1_000_000).toString();
        // Amitis monetary module requires 0.3% fee on MsgSend amount
        const feeAmt = Math.max(Math.ceil(parseInt(uamts) * 0.003), 30000).toString();
        const fee    = { amount: [{ denom: 'uamts', amount: feeAmt }], gas: '200000' };
        result = await client.sendTokens(
          address, sendTo,
          [{ denom: 'uamts', amount: uamts }],
          fee, sendMemo
        );
      } else {
        throw new Error('No wallet connected');
      }
      setSendResult({ ok: true, hash: result.transactionHash, msg: `✓ Sent ${sendAmt} AMTS` });
      setSendTo(''); setSendAmt(''); setSendMemo('');
      setTimeout(() => { refresh(); loadTxs(); loadAllTxs(); }, 4000);
    } catch (err) {
      setSendResult({ ok: false, msg: err.message || 'Transaction failed' });
    }
    setSendLoading(false);
  }

  const tokens = [
    { denom: 'uamts',  icon: 'amts', sym: 'A',  name: 'Amitis',       sub: 'AMTS · native',    price: AMTS_PRICE },
    { denom: 'ncd001', icon: 'ncd',  sym: 'N',  name: 'Sovereign NCD',sub: 'NCD-001 · RWA',     price: 1 },
    { denom: 'uosmo',  icon: 'osmo', sym: 'O',  name: 'Osmosis',      sub: 'OSMO · IBC',        price: null },
    { denom: 'uatom',  icon: 'atom', sym: '⚛',  name: 'Cosmos',       sub: 'ATOM · IBC',        price: null },
  ];

  function txType(tx, currentAddress) {
    const t   = tx.tx?.body?.messages?.[0]?.['@type'] || '';
    const msg = tx.tx?.body?.messages?.[0];
    if (t.includes('Swap'))    return { icon: 'swap', char: '⇌', label: 'Swap' };
    if (t.includes('Delegate'))return { icon: 'recv', char: '⚡', label: 'Stake' };
    if (t.includes('MsgSend')) {
      // Determine direction by comparing sender to current address
      const isSend = msg?.from_address === currentAddress;
      return isSend
        ? { icon: 'send', char: '↑', label: 'Send' }
        : { icon: 'recv', char: '↓', label: 'Received' };
    }
    return { icon: 'recv', char: '↓', label: 'Transaction' };
  }

  return (
    <div className="page-content fade-up">
      {/* Stats */}
      <div className="stats-row">
        <div className="stat-card fade-up">
          <div className="stat-label">Portfolio Value</div>
          <div className="stat-value gold">{address ? usd : '$—'}</div>
          <div className="stat-sub">@ $7.50 / AMTS</div>
        </div>
        <div className="stat-card fade-up-1">
          <div className="stat-label">AMTS Balance</div>
          <div className="stat-value" style={{ color: 'var(--green)' }}>
            {address ? fmtBal : '—'}
          </div>
          <div className="stat-sub">AMTS</div>
        </div>
        <div className="stat-card fade-up-2">
          <div className="stat-label">Active Validators</div>
          <div className="stat-value">{netStats.validators || '—'}</div>
          <div className="stat-sub">of 21 petals</div>
        </div>
        <div className="stat-card fade-up-3">
          <div className="stat-label">Fee Rebate</div>
          <div className="stat-value" style={{ color: 'var(--green)' }}>80%</div>
          <div className="stat-sub">hardcoded · immutable</div>
        </div>
      </div>

      <div className="grid-2" style={{ gap: 16, marginBottom: 20 }}>
        {/* Holdings */}
        <div className="card fade-up-1">
          <div className="card-header">
            <div className="card-title">Holdings</div>
            <div style={{ display: 'flex', gap: 10 }}>
              {address && (
                <span
                  className="card-action"
                  onClick={() => setShowSend(s => !s)}
                  style={{ color: showSend ? 'var(--muted)' : 'var(--green)' }}
                >
                  {showSend ? 'Cancel' : 'Send →'}
                </span>
              )}
              <Link to="/swap" className="card-action">Swap →</Link>
            </div>
          </div>

          {/* Send form */}
          {showSend && address && (
            <div style={{
              background: 'var(--off-black)',
              border: '1px solid var(--border)',
              borderRadius: 12,
              padding: '16px',
              marginBottom: 16,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--white)', marginBottom: 12 }}>
                Send AMTS
              </div>
              <div className="input-group" style={{ marginBottom: 10 }}>
                <label className="input-label">Recipient</label>
                <input
                  className="input-field"
                  placeholder="amitis1..."
                  value={sendTo}
                  onChange={e => setSendTo(e.target.value)}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div className="input-group" style={{ marginBottom: 0 }}>
                  <label className="input-label">Amount (AMTS)</label>
                  <input
                    className="input-field"
                    type="number"
                    placeholder="0.000000"
                    value={sendAmt}
                    onChange={e => setSendAmt(e.target.value)}
                  />
                </div>
                <div className="input-group" style={{ marginBottom: 0 }}>
                  <label className="input-label">Memo (optional)</label>
                  <input
                    className="input-field"
                    placeholder=""
                    value={sendMemo}
                    onChange={e => setSendMemo(e.target.value)}
                  />
                </div>
              </div>
              {sendAmt && parseFloat(sendAmt) > 0 && (
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  fontFamily: 'DM Mono, monospace', fontSize: 10,
                  color: 'var(--dimmer)', marginBottom: 8, padding: '0 2px'
                }}>
                  <span>Fee (0.3%)</span>
                  <span style={{ color: 'var(--green)' }}>
                    {Math.max(Math.ceil(parseFloat(sendAmt) * 1_000_000 * 0.003), 30000) / 1_000_000} AMTS
                    <span style={{ color: 'var(--dimmer)', marginLeft: 4 }}>→ 80% rebated</span>
                  </span>
                </div>
              )}
              <button
                className="btn btn-primary btn-sm btn-full"
                onClick={handleSend}
                disabled={sendLoading || !sendTo || !sendAmt}
              >
                {sendLoading ? <><div className="spinner"></div> Sending…</> : `Send ${sendAmt || '0'} AMTS`}
              </button>
              {sendResult && (
                <div style={{
                  marginTop: 10, padding: '8px 12px', borderRadius: 8,
                  background: sendResult.ok ? 'var(--green-dim)' : 'var(--red-dim)',
                  border: `1px solid ${sendResult.ok ? 'var(--green-border)' : 'rgba(248,113,113,0.25)'}`,
                  color: sendResult.ok ? 'var(--green)' : 'var(--red)',
                  fontFamily: 'DM Mono, monospace', fontSize: 11,
                }}>
                  {sendResult.msg}
                  {sendResult.hash && <div style={{ marginTop: 4, opacity: 0.7, fontSize: 10 }}>TX: {sendResult.hash.slice(0,20)}…</div>}
                </div>
              )}
            </div>
          )}

          {tokens.map(t => {
            const amt = address ? getAmt(t.denom) : null;
            const isNative = t.denom === 'uamts';
            const fmt = amt !== null
              ? (amt === 0 && !isNative
                  ? '—'
                  : amt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 }))
              : '—';
            return (
              <div className="token-row" key={t.denom}>
                <div className={`token-icon ${t.icon}`}>{t.sym}</div>
                <div>
                  <div className="token-name">{t.name}</div>
                  <div className="token-sub">{t.sub}</div>
                </div>
                <div className="token-right">
                  <div className="token-amount">{fmt}</div>
                  <div className="token-usd">
                    {amt !== null && t.price && (amt > 0 || t.denom === 'uamts')
                      ? `≈ $${(amt * t.price).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                      : t.price === null ? (amt === 0 ? '—' : 'via IBC') : '—'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Transactions */}
        <div className="card fade-up-2">
          <div className="card-header">
            <div className="card-title">
              {address ? 'My Transactions' : 'Recent Transactions'}
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              {!address && (
                <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 9, color: 'var(--dimmer)', letterSpacing: '0.1em' }}>
                  CHAIN-WIDE
                </span>
              )}
              <span className="card-action" onClick={address ? loadTxs : loadChainTxs}>↺</span>
              <Link to="/explorer" className="card-action">Explorer →</Link>
            </div>
          </div>
          {address ? (
            loadingTx ? (
              <div className="loading-row"><div className="spinner"></div> Loading…</div>
            ) : txs.length === 0 ? (
              <div className="loading-row" style={{ flexDirection: 'column', gap: 8 }}>
                <div>No transactions yet</div>
                {balance === 0 && (
                  <Link to="/faucet" className="card-action" style={{ fontSize: 12 }}>Claim from faucet →</Link>
                )}
              </div>
            ) : txs.map((tx, i) => {
              const t    = txType(tx, address);
              const hash = tx.txhash ? tx.txhash.slice(0,8) + '…' + tx.txhash.slice(-6) : '—';
              const time = tx.timestamp ? new Date(tx.timestamp).toLocaleDateString() : '—';
              return (
                <div className="tx-row" key={i}>
                  <div className={`tx-icon ${t.icon}`}>{t.char}</div>
                  <div>
                    <div className="tx-desc">{t.label}</div>
                    <div className="tx-hash">{hash}</div>
                  </div>
                  <div className={tx.code === 0 ? 'tx-amount-pos' : 'tx-amount-neg'} style={{ fontSize: 11 }}>
                    {tx.code === 0 ? '✓' : '✗'} {time}
                  </div>
                </div>
              );
            })
          ) : loadingChain ? (
            <div className="loading-row"><div className="spinner"></div> Loading chain activity…</div>
          ) : chainTxs.length === 0 ? (
            <div className="loading-row">No recent transactions</div>
          ) : chainTxs.map((tx, i) => {
            const t    = txType(tx, address);
            const hash = tx.txhash ? tx.txhash.slice(0,8) + '…' + tx.txhash.slice(-6) : '—';
            const time = tx.timestamp ? new Date(tx.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : `#${tx.height}`;
            const msg  = tx.tx?.body?.messages?.[0];
            let amount = '';
            if (msg?.amount) {
              const coins = Array.isArray(msg.amount) ? msg.amount : [msg.amount];
              const u = coins.find(c => c.denom === 'uamts');
              if (u) amount = (parseInt(u.amount) / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 }) + ' AMTS';
            }
            return (
              <div className="tx-row" key={i}>
                <div className="tx-icon swap" style={{ fontSize: 11 }}>⬡</div>
                <div>
                  <div className="tx-desc">{amount || t.label}</div>
                  <div className="tx-hash">{hash}</div>
                </div>
                <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 10, color: 'var(--dimmer)', textAlign: 'right' }}>
                  {time}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Transaction History */}
      {address && (
        <div className="card fade-up-3" style={{ marginBottom: 20 }}>
          <div className="card-header">
            <div className="card-title">Transaction History</div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 10, color: 'var(--dimmer)' }}>
                last 25 · auto-refreshes
              </span>
              <span className="card-action" onClick={loadAllTxs}>↺ Refresh</span>
              <Link to="/explorer" className="card-action">Explorer →</Link>
            </div>
          </div>
          {loadingAllTx ? (
            <div className="loading-row"><div className="spinner"></div> Loading…</div>
          ) : allTxs.length === 0 ? (
            <div className="loading-row">No transactions found</div>
          ) : (
            <div>
              {/* Header row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '28px 80px 1fr 120px 100px 80px',
                gap: 12,
                padding: '6px 0 10px',
                borderBottom: '1px solid var(--border)',
                marginBottom: 4,
              }}>
                {['', 'Type', 'Hash', 'From / To', 'Amount', 'Time'].map((h, i) => (
                  <span key={i} style={{
                    fontFamily: 'DM Mono, monospace', fontSize: 9,
                    color: 'var(--dimmer)', letterSpacing: '0.15em', textTransform: 'uppercase'
                  }}>{h}</span>
                ))}
              </div>
              {allTxs.map((tx, i) => {
                const t     = txType(tx);
                const hash  = tx.txhash ? tx.txhash.slice(0,10) + '…' + tx.txhash.slice(-6) : '—';
                const time  = tx.timestamp ? new Date(tx.timestamp).toLocaleString([], {
                  month: 'short', day: 'numeric',
                  hour: '2-digit', minute: '2-digit'
                }) : '—';
                // Try to extract amount and counterparty from messages
                const msg   = tx.tx?.body?.messages?.[0];
                const msgType = msg?.['@type']?.split('.').pop()?.replace('Msg','') || '—';
                let amount  = '—';
                let counter = '—';
                if (msg?.amount) {
                  const coins = Array.isArray(msg.amount) ? msg.amount : [msg.amount];
                  const uamts = coins.find(c => c.denom === 'uamts');
                  if (uamts) amount = (parseInt(uamts.amount) / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 4 }) + ' AMTS';
                }
                if (msg?.to_address) counter = msg.to_address.slice(0,12) + '…';
                else if (msg?.from_address && msg.from_address !== address) counter = msg.from_address.slice(0,12) + '…';

                return (
                  <div key={i} style={{
                    display: 'grid',
                    gridTemplateColumns: '28px 80px 1fr 120px 100px 80px',
                    gap: 12,
                    alignItems: 'center',
                    padding: '9px 0',
                    borderBottom: '1px solid var(--border)',
                    transition: 'opacity 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                  >
                    <div className={`tx-icon ${t.icon}`} style={{ width: 24, height: 24, fontSize: 10 }}>{t.char}</div>
                    <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, color: 'var(--muted)' }}>{msgType}</div>
                    <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, color: 'var(--dimmer)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {hash}
                    </div>
                    <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 10, color: 'var(--dimmer)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {counter}
                    </div>
                    <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, color: tx.code === 0 ? 'var(--green)' : 'var(--red)' }}>
                      {amount}
                    </div>
                    <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 10, color: 'var(--dimmer)', textAlign: 'right' }}>
                      {time}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Protocol Stats */}
      <div className="card fade-up-3">
        <div className="card-header">
          <div className="card-title">Protocol Stats</div>
          <Link to="/validators" className="card-action">Validators →</Link>
        </div>
        <div className="stats-row" style={{ marginBottom: 0 }}>
          <div className="stat-card">
            <div className="stat-label">Latest Block</div>
            <div className="stat-value">{blockHeight || '—'}</div>
            <div className="stat-sub">~500ms finality</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Community Pool</div>
            <div className="stat-value" style={{ color: 'var(--gold)' }}>{netStats.pool || '—'}</div>
            <div className="stat-sub">AMTS</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Total Supply</div>
            <div className="stat-value">1B</div>
            <div className="stat-sub">AMTS · fixed</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Chain ID</div>
            <div className="stat-value" style={{ fontSize: 16, fontFamily: 'DM Mono', letterSpacing: '-0.01em' }}>amitis-network</div>
            <div className="stat-sub">Cosmos SDK v0.50</div>
          </div>
        </div>
      </div>
    </div>
  );
}
